import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, MapPin, Leaf, BookOpen } from "lucide-react";
import api, { buyLink } from "@/lib/api";

export default function Home() {
  const [parks, setParks] = useState([]);
  const [products, setProducts] = useState([]);
  const [posts, setPosts] = useState([]);

  useEffect(() => {
    api.get("/parks").then((r) => setParks(r.data));
    api.get("/products", { params: { featured: true } }).then((r) => setProducts(r.data));
    api.get("/blog").then((r) => setPosts(r.data.slice(0, 3)));
  }, []);

  const refuges = parks.filter((p) => p.type === "wildlife_refuge");

  return (
    <div>
      {/* HERO */}
      <section className="relative overflow-hidden grain">
        <div className="max-w-7xl mx-auto px-6 md:px-10 pt-16 md:pt-24 pb-24 md:pb-32 grid grid-cols-1 lg:grid-cols-12 gap-12 items-end">
          <div className="lg:col-span-7">
            <motion.div
              initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
              className="label-eyebrow mb-6 flex items-center gap-3"
            >
              <span className="w-8 h-px bg-[#C95B3B]" />
              Effective September 1, 2026
            </motion.div>
            <motion.h1
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.05 }}
              className="font-display text-5xl md:text-6xl lg:text-7xl leading-[0.95] tracking-tight mb-8"
            >
              Fish the parks<br />
              you love —<br />
              <span className="italic text-[#C95B3B]">without the lead.</span>
            </motion.h1>
            <motion.p
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.7, delay: 0.15 }}
              className="text-lg text-muted-foreground max-w-xl leading-relaxed mb-10"
            >
              Seven National Wildlife Refuges go lead-free on September 1, 2026.
              We track every rule change, map every affected water, and stock the gear that keeps you legal.
            </motion.p>
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.7, delay: 0.25 }}
              className="flex flex-wrap gap-3"
            >
              <Link to="/regulations" className="btn-accent" data-testid="hero-cta-map">
                <MapPin className="w-4 h-4" /> See the regulations map
              </Link>
              <Link to="/gear" className="btn-ghost" data-testid="hero-cta-gear">
                Shop eco gear <ArrowRight className="w-4 h-4" />
              </Link>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.8, delay: 0.1 }}
            className="lg:col-span-5 relative"
          >
            <div className="aspect-[4/5] w-full overflow-hidden border border-border">
              <img
                src="https://images.pexels.com/photos/27207261/pexels-photo-27207261.jpeg"
                alt="Fisherman casts at dawn"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="absolute -bottom-6 -left-6 bg-[#F5F2EB] border border-foreground p-5 shadow-[8px_8px_0_#1A2421] max-w-[240px]">
              <div className="label-eyebrow mb-1 text-[#2D5A4C]">Refuges affected</div>
              <div className="font-display text-5xl leading-none">{refuges.length || 8}</div>
              <div className="text-xs text-muted-foreground mt-2">National Wildlife Refuges shift to non-lead tackle on Sept 1, 2026.</div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* THREE PILLARS */}
      <section className="border-t border-border bg-[#E6E2D6]">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 grid grid-cols-1 md:grid-cols-3 gap-6">
          {[
            { icon: MapPin, title: "Know the Rules", body: "Every park, refuge, and effective date on one interactive map.", to: "/regulations", cta: "Open the map" },
            { icon: Leaf, title: "Fish Approved Gear", body: "Curated lead-free, biodegradable, and recycled tackle.", to: "/gear", cta: "Browse gear" },
            { icon: BookOpen, title: "Read the Journal", body: "Reviews, guides, and stories from waters worth protecting.", to: "/journal", cta: "Read the journal" },
          ].map((p, i) => (
            <motion.div
              key={p.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.1 }}
              className="bg-white p-8 border border-border card-flat"
              data-testid={`pillar-${p.to.replace('/', '')}`}
            >
              <p.icon className="w-8 h-8 text-[#2D5A4C] mb-6" strokeWidth={1.5} />
              <h3 className="font-display text-2xl mb-3">{p.title}</h3>
              <p className="text-sm text-muted-foreground mb-6 leading-relaxed">{p.body}</p>
              <Link to={p.to} className="text-sm font-semibold text-[#C95B3B] hover:underline inline-flex items-center gap-1">
                {p.cta} <ArrowRight className="w-4 h-4" />
              </Link>
            </motion.div>
          ))}
        </div>
      </section>

      {/* FEATURED PRODUCTS */}
      <section>
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-24">
          <div className="flex justify-between items-end mb-12">
            <div>
              <div className="label-eyebrow mb-3">Curated for the new rules</div>
              <h2 className="font-display text-4xl md:text-5xl">Gear that qualifies —<br />anywhere you fish.</h2>
            </div>
            <Link to="/gear" className="hidden md:inline-flex btn-ghost">All gear <ArrowRight className="w-4 h-4" /></Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {products.slice(0, 3).map((p) => (
              <a key={p.id} href={buyLink(p.id, "home")} target="_blank" rel="noreferrer" className="card-flat block group" data-testid={`home-product-${p.id}`}>
                <div className="aspect-square bg-[#E6E2D6] overflow-hidden">
                  <img src={p.image_url} alt={p.name} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                </div>
                <div className="p-5">
                  <div className="flex flex-wrap gap-1 mb-3">
                    {p.badges.map((b) => <span key={b} className="eco-badge">{b}</span>)}
                  </div>
                  <div className="label-eyebrow mb-1">{p.brand}</div>
                  <div className="font-display text-xl leading-tight mb-1">{p.name}</div>
                  <div className="text-sm text-muted-foreground">{p.price}</div>
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* JOURNAL */}
      <section className="border-t border-border">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-24">
          <div className="flex justify-between items-end mb-12">
            <div>
              <div className="label-eyebrow mb-3">From the journal</div>
              <h2 className="font-display text-4xl md:text-5xl">Fish smarter, cleaner.</h2>
            </div>
            <Link to="/journal" className="hidden md:inline-flex btn-ghost">All posts <ArrowRight className="w-4 h-4" /></Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {posts.map((post) => (
              <Link key={post.id} to={`/journal/${post.slug}`} className="card-flat block group" data-testid={`home-post-${post.slug}`}>
                <div className="aspect-[4/3] overflow-hidden bg-[#E6E2D6]">
                  <img src={post.image_url} alt={post.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                </div>
                <div className="p-6">
                  <div className="label-eyebrow mb-2 text-[#C95B3B]">{post.tag}</div>
                  <h3 className="font-display text-2xl leading-tight mb-2">{post.title}</h3>
                  <p className="text-sm text-muted-foreground line-clamp-2">{post.excerpt}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
